import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, X, Heart } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "ew_intro_referral_v1";
const DISMISS_MS_NOT_NOW = 30 * 24 * 60 * 60 * 1000; // 30 days
const DISMISS_MS_SUBMITTED = 180 * 24 * 60 * 60 * 1000; // 180 days
const OPEN_DELAY_MS = 2200;

const initial = {
  name: "",
  organisation: "",
  phone: "",
  email: "",
  message: "",
};

const readState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
};

export const IntroReferralModal = () => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => {
    const state = readState();
    const now = Date.now();
    if (state && state.until && state.until > now) return; // still snoozed
    const t = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close("not-now");
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => nameRef.current?.focus(), 200);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open]);

  const close = (reason) => {
    setOpen(false);
    const ms =
      reason === "submitted" ? DISMISS_MS_SUBMITTED : DISMISS_MS_NOT_NOW;
    writeState({ reason, until: Date.now() + ms });
  };

  const onChange = (e) =>
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!values.name || !values.phone || !values.email || !values.message) {
      toast.error("Please share your name, phone, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", values.name.trim());
      if (values.organisation?.trim())
        fd.append("organisation", values.organisation.trim());
      fd.append("phone", values.phone.trim());
      fd.append("email", values.email.trim());
      fd.append("message", values.message.trim());
      fd.append("kind", "referral");
      await axios.post(`${API}/contact`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Thank you. We will be in touch within one business day.");
      setValues(initial);
      close("submitted");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please call 0450 270 547.";
      toast.error(
        typeof msg === "string" ? msg : "Unable to send. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="intro-title"
      data-testid="intro-referral-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-6"
      onClick={(e) => {
        if (e.target === dialogRef.current?.parentElement) close("not-now");
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-[var(--ew-text)]/55 backdrop-blur-sm"
        onClick={() => close("not-now")}
      />

      <div
        ref={dialogRef}
        className="relative w-full max-w-lg bg-white rounded-3xl border border-[#6E9C88]/25 shadow-[0_30px_80px_rgba(46,80,65,0.28)] overflow-hidden animate-[ew-rise_0.35s_ease_both] max-h-[92vh] flex flex-col"
      >
        <button
          type="button"
          data-testid="intro-close"
          onClick={() => close("not-now")}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-[var(--ew-text-2)] hover:bg-[var(--ew-bg-3)] hover:text-[var(--ew-accent)] transition-colors"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>

        <div
          className="px-6 sm:px-8 pt-8 pb-6"
          style={{
            background:
              "linear-gradient(160deg, rgba(163,196,211,0.35) 0%, rgba(110,156,136,0.15) 60%, rgba(255,255,255,0) 100%)",
          }}
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#6E9C88]/25 px-3 py-1">
            <Heart
              className="h-3.5 w-3.5 text-[var(--ew-accent)]"
              strokeWidth={2}
            />
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--ew-accent)]">
              Say Hello
            </span>
          </div>
          <h2
            id="intro-title"
            className="font-heading text-2xl sm:text-[1.7rem] font-semibold text-[var(--ew-text)] mt-4 leading-tight"
          >
            Care, coordination and connection.
          </h2>
          <p className="mt-2 text-sm sm:text-base text-[var(--ew-text-2)] leading-relaxed">
            RN-led community nursing, support coordination and community
            access. Tell us a little — we reply within one business day.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="px-6 sm:px-8 pb-6 space-y-3 overflow-y-auto"
          noValidate
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="intro-name"
                className="block text-xs font-medium text-[var(--ew-text)] mb-1.5"
              >
                Full name <span className="text-[var(--ew-accent)]">*</span>
              </label>
              <input
                id="intro-name"
                ref={nameRef}
                name="name"
                data-testid="intro-name"
                value={values.name}
                onChange={onChange}
                className="ew-input h-11"
                placeholder="Jane Smith"
                required
              />
            </div>
            <div>
              <label
                htmlFor="intro-organisation"
                className="block text-xs font-medium text-[var(--ew-text)] mb-1.5"
              >
                Organisation{" "}
                <span className="text-[var(--ew-text-muted)] font-normal">
                  (optional)
                </span>
              </label>
              <input
                id="intro-organisation"
                name="organisation"
                data-testid="intro-organisation"
                value={values.organisation}
                onChange={onChange}
                className="ew-input h-11"
                placeholder="e.g. Support coordination team"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="intro-phone"
                className="block text-xs font-medium text-[var(--ew-text)] mb-1.5"
              >
                Phone <span className="text-[var(--ew-accent)]">*</span>
              </label>
              <input
                id="intro-phone"
                type="tel"
                name="phone"
                data-testid="intro-phone"
                value={values.phone}
                onChange={onChange}
                className="ew-input h-11"
                placeholder="04xx xxx xxx"
                required
              />
            </div>
            <div>
              <label
                htmlFor="intro-email"
                className="block text-xs font-medium text-[var(--ew-text)] mb-1.5"
              >
                Email <span className="text-[var(--ew-accent)]">*</span>
              </label>
              <input
                id="intro-email"
                type="email"
                name="email"
                data-testid="intro-email"
                value={values.email}
                onChange={onChange}
                className="ew-input h-11"
                placeholder="name@example.com"
                required
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="intro-message"
              className="block text-xs font-medium text-[var(--ew-text)] mb-1.5"
            >
              How can we help?{" "}
              <span className="text-[var(--ew-accent)]">*</span>
            </label>
            <textarea
              id="intro-message"
              name="message"
              data-testid="intro-message"
              value={values.message}
              onChange={onChange}
              className="ew-input"
              placeholder="A short note about what you're after — we'll take it from there."
              rows={3}
              required
            />
          </div>

          <div className="flex flex-col sm:flex-row-reverse gap-2.5 sm:items-center pt-1">
            <button
              type="submit"
              data-testid="intro-submit"
              disabled={submitting}
              className="btn-primary w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Reach Out <Send className="h-4 w-4" strokeWidth={2} />
                </>
              )}
            </button>
            <button
              type="button"
              data-testid="intro-not-now"
              onClick={() => close("not-now")}
              className="text-sm font-medium text-[var(--ew-text-muted)] hover:text-[var(--ew-accent)] transition-colors sm:mr-auto"
            >
              Not right now
            </button>
          </div>
          <p className="text-[11px] text-[var(--ew-text-muted)] leading-relaxed pt-1">
            For urgent or life-threatening situations, call{" "}
            <a
              href="tel:000"
              className="font-semibold text-[var(--ew-accent)]"
            >
              Triple Zero (000)
            </a>
            . Everwell is not an emergency response service.
          </p>
        </form>
      </div>
    </div>
  );
};

export default IntroReferralModal;
